<script setup>
import axios from 'axios';
import { BBadge, BTable } from 'bootstrap-vue-next';
import { ref } from 'vue';
import { useRoute } from 'vue-router';

import ErrorState from '~/common/components/ErrorState.vue';
import FilterCard from '~/common/components/FilterCard.vue';
import RoutePagination from '~/common/components/RoutePagination.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import { dateTimeFormatter } from '~/common/FieldFormats';

const route = useRoute();
const { platform } = route.params;
const pageTitle = route.meta.title;

const fields = [
  { key: 'occurred_at', label: 'Date' },
  { key: 'kind', label: 'Type' },
  { key: 'title', label: 'Description' },
  { key: 'user', label: 'Contributor' },
];

const activities = ref([]);
const busy = ref(true);
const error = ref(null);

const filterData = ref({
  search: '',
  fromDate: '',
  toDate: '',
  communityOnly: true,
  currentPage: 1,
});

const pageData = ref({
  perPage: 20,
  totalRows: 0,
});

const fetchData = async (newPage) => {
  busy.value = true;
  error.value = null;

  if (typeof newPage === 'number') {
    filterData.value.currentPage = newPage;
  }

  const query = {
    platform: platform,
    from_date: filterData.value.fromDate,
    to_date: filterData.value.toDate,
    community_only: filterData.value.communityOnly,
    page: filterData.value.currentPage,
    search: filterData.value.search,
  };

  try {
    const response = await axios.get('/api/v1/activities', { params: query });
    const { records, metadata } = response.data;

    activities.value = records;
    pageData.value = {
      perPage: metadata.per_page,
      totalRows: metadata.total_count,
    };
  } catch (e) {
    error.value = e;
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <ErrorState v-if="error" />

  <template v-else>
    <div>
      <h2 class="my-3">{{ pageTitle }}</h2>

      <FilterCard
        v-model="filterData"
        show-search
        show-community-only
        @filter="fetchData"
      />

      <ThemedOverlay :show="busy">
        <BTable
          :items="activities"
          :fields="fields"
          hover
          striped
          responsive
          show-empty
        >
          <template #cell(occurred_at)="{ value }">
            {{ dateTimeFormatter(value) }}
          </template>

          <template #cell(kind)="{ value }">
            <BBadge>{{ value }}</BBadge>
          </template>

          <template #cell(title)="{ value, item }">
            <a
              v-if="item.url"
              :href="item.url"
              target="_blank"
            >
              {{ value }}
            </a>
            <template v-else>{{ value }}</template>
          </template>

          <template #cell(user)="{ value }">
            <a
              v-if="value && value.web_url"
              :href="value.web_url"
              target="_blank"
            >
              {{ value.username }}
            </a>
            <span v-else-if="value">{{ value.username }}</span>
            <span
              v-else
              class="text-muted"
            >Unknown</span>
          </template>

          <template #empty>
            <div class="text-center p-3">
              No activities found for this period.
            </div>
          </template>
        </BTable>

        <RoutePagination
          v-model="filterData.currentPage"
          :total-rows="pageData.totalRows"
          :per-page="pageData.perPage"
          @update:model-value="fetchData"
        />
      </ThemedOverlay>
    </div>
  </template>
</template>
